import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import {
  allowedAttributes,
  usedAttributes,
  attributeOptionLabel,
} from './columnAttributes';

var MENU_WIDTH = 180;

/**
 * The attribute chip on a selected column, and the menu it opens.
 *
 * The chip reuses the shared .preview-table-column-attribute treatment, so the
 * control in the configurator is visually the chip that appears in the rendered
 * table header. Choosing applies immediately: this edits local draft state and
 * issues no request, so there is nothing to confirm.
 */
var ColumnAttributeControl = function (props) {
  var field = props.field;
  var fields = props.fields;
  var isRecordListView = props.isRecordListView;
  var onChange = props.onChange;
  var t = props.t;

  var _anchorState = useState(null);
  var anchorRect = _anchorState[0];
  var setAnchorRect = _anchorState[1];

  useEffect(
    function () {
      if (!anchorRect) return undefined;
      var handleKeyDown = function (e) {
        if (e.key === 'Escape') setAnchorRect(null);
      };
      document.addEventListener('keydown', handleKeyDown);
      return function () {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [anchorRect],
  );

  var allowed = allowedAttributes(field, isRecordListView);
  if (allowed.length < 2) return null;

  var current = field.fieldAttribute || allowed[0];
  var taken = usedAttributes(
    fields,
    field.suuid || field.fieldSUUID,
    field.cuuid,
  );

  var handleTriggerClick = function (e) {
    e.stopPropagation();
    if (anchorRect) {
      setAnchorRect(null);
    } else {
      setAnchorRect(e.currentTarget.getBoundingClientRect());
    }
  };

  // The row is draggable; swallow mousedown so pressing the chip does not
  // start a column drag (the remove button does the same).
  var handleTriggerMouseDown = function (e) {
    e.stopPropagation();
  };

  // The control is rendered inside a SlateDialog, which closes on Escape.
  // React's synthetic Escape bubbles from the focused trigger button up
  // through the component tree to the Dialog's own onKeyDown, so without this
  // guard, dismissing the attribute menu also dismisses the whole modal.
  // Only swallow the key while the menu is actually open.
  var handleTriggerKeyDown = function (e) {
    if (anchorRect && e.key === 'Escape') {
      e.stopPropagation();
      setAnchorRect(null);
    }
  };

  var handleClose = function () {
    setAnchorRect(null);
  };

  var handleSelect = function (attribute) {
    setAnchorRect(null);
    if (attribute !== current && onChange) {
      onChange(field.cuuid, attribute);
    }
  };

  var menuStyle = null;
  if (anchorRect) {
    menuStyle = {
      top: anchorRect.bottom + 6,
      left: Math.max(
        8,
        Math.min(anchorRect.left, window.innerWidth - MENU_WIDTH - 8),
      ),
    };
  }

  return (
    <React.Fragment>
      <button
        type="button"
        className="preview-table-column-attribute column-attribute-chip"
        onClick={handleTriggerClick}
        onMouseDown={handleTriggerMouseDown}
        onKeyDown={handleTriggerKeyDown}
        draggable={false}
        aria-haspopup="true"
        aria-expanded={anchorRect ? 'true' : 'false'}
      >
        {attributeOptionLabel(current, t)}
        <span className="material-symbols-outlined">expand_more</span>
      </button>
      {anchorRect &&
        ReactDOM.createPortal(
          <div className="column-attribute-overlay" onClick={handleClose}>
            <div
              className="column-attribute-menu"
              style={menuStyle}
              onClick={function (e) {
                e.stopPropagation();
              }}
            >
              {allowed.map(function (attribute) {
                var isCurrent = attribute === current;
                var isTaken = !isCurrent && taken.indexOf(attribute) !== -1;
                return (
                  <button
                    key={attribute}
                    type="button"
                    className={
                      'column-attribute-option' +
                      (isCurrent ? ' column-attribute-option--current' : '')
                    }
                    disabled={isTaken}
                    onClick={function () {
                      handleSelect(attribute);
                    }}
                  >
                    <span>{attributeOptionLabel(attribute, t)}</span>
                    {isCurrent && (
                      <span className="material-symbols-outlined">check</span>
                    )}
                    {isTaken && (
                      <span className="column-attribute-option-hint">
                        {t('settings:fieldAttributeInUse')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </React.Fragment>
  );
};

ColumnAttributeControl.propTypes = {
  field: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  isRecordListView: PropTypes.bool,
  onChange: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default ColumnAttributeControl;
